import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Trophy, Target, Users, Calendar, TrendingUp, Award, ChevronDown, ChevronUp, ExternalLink, Medal } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { userStatsAPI } from '../services/userStatsApi';
import { formatPercentage } from '../../scoring/pingpong/stats/utils/statsFormatters';

/**
 * PlayerStatistics Component
 * Displays overall and sport-specific statistics for a player
 */
export default function PlayerStatistics({ userId, onSportsLoaded }) {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [expandedSport, setExpandedSport] = useState(null);
  const [sportDetails, setSportDetails] = useState({}); // { sportName: detailData }
  const [loadingSport, setLoadingSport] = useState(null);

  useEffect(() => {
    if (!userId) return;

    const fetchStats = async () => {
      setLoading(true);
      try {
        const data = await userStatsAPI.getOverallStats(userId);
        setStats(data);
        if (onSportsLoaded && data.sportsPlayed) onSportsLoaded(data.sportsPlayed);
      } catch (error) {
        console.error('Failed to fetch player stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [userId, location.key]);

  const SPORTS_WITH_DETAIL = ['Cricket', 'Basketball', 'Tennis', 'Ping Pong', 'Pickleball', 'Volleyball'];

  const toggleSport = async (sportName) => {
    if (expandedSport === sportName) {
      setExpandedSport(null);
      return;
    }
    setExpandedSport(sportName);
    // Only fetch detail for sports that have a detail stats endpoint
    if (SPORTS_WITH_DETAIL.includes(sportName) && !sportDetails[sportName]) {
      setLoadingSport(sportName);
      try {
        const detail = await userStatsAPI.getSportStats(userId, encodeURIComponent(sportName));
        setSportDetails(prev => ({ ...prev, [sportName]: detail }));
      } catch (error) {
        console.error(`Failed to fetch ${sportName} stats:`, error);
      } finally {
        setLoadingSport(null);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className={`${theme.card} rounded-lg border ${theme.border} p-8 text-center`}>
        <p className={theme.textMuted}>
          No statistics available yet. Start playing to see your stats!
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overall Statistics Cards */}
      <div>
        <h3 className={`text-lg font-semibold ${theme.text} mb-4`}>
          Overall Statistics
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Total Matches */}
          <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
            <div className="flex items-center gap-2 mb-2">
              <Trophy className="w-5 h-5 text-blue-500" />
              <span className={`text-sm font-medium ${theme.textMuted}`}>Matches</span>
            </div>
            <p className={`text-2xl font-bold ${theme.text}`}>{stats.totalMatches}</p>
            <p className={`text-xs ${theme.textMuted} mt-1`}>
              {stats.totalWins}W - {stats.totalLosses}L
            </p>
          </div>

          {/* Win Rate */}
          <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              <span className={`text-sm font-medium ${theme.textMuted}`}>Win Rate</span>
            </div>
            <p className={`text-2xl font-bold ${theme.text}`}>
              {formatPercentage(stats.overallWinRate)}
            </p>
            <p className={`text-xs ${theme.textMuted} mt-1`}>
              {stats.totalWins}/{stats.totalMatches} matches
            </p>
          </div>

          {/* Teams */}
          <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-purple-500" />
              <span className={`text-sm font-medium ${theme.textMuted}`}>Teams</span>
            </div>
            <p className={`text-2xl font-bold ${theme.text}`}>{stats.teamsCount}</p>
            <p className={`text-xs ${theme.textMuted} mt-1`}>Member of</p>
          </div>

          {/* Tournaments */}
          <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-5 h-5 text-yellow-500" />
              <span className={`text-sm font-medium ${theme.textMuted}`}>Tournaments</span>
            </div>
            <p className={`text-2xl font-bold ${theme.text}`}>{stats.tournamentsCount}</p>
            <p className={`text-xs ${theme.textMuted} mt-1`}>Participated</p>
          </div>
        </div>
      </div>

      {/* Sport Involvement */}
      {stats.sportsPlayed && stats.sportsPlayed.length > 0 && stats.totalMatches > 0 && (
        <div className={`${theme.card} rounded-lg border ${theme.border} p-6`}>
          <h4 className={`text-md font-semibold ${theme.text} mb-4`}>Sport Involvement</h4>
          <div className="space-y-3">
            {stats.sportsPlayed.map((sport) => {
              /* ⚠️ A SPORT SOMEBODY HAS PLAYED MUST NEVER READ 0%. One cricket
               * match in three hundred rounds to zero, so the row said "Cricket
               * 0% (1)" beside an empty bar — which reads as "never played it"
               * next to a count saying otherwise. Round-to-zero becomes "<1%",
               * and the bar keeps a sliver so the sport is visibly there. */
              const exact = (sport.matchesPlayed / stats.totalMatches) * 100;
              const pct = Math.round(exact);
              const shown = pct === 0 && sport.matchesPlayed > 0 ? '<1%' : `${pct}%`;
              const barWidth = Math.max(pct, sport.matchesPlayed > 0 ? 1.5 : 0);
              return (
                <div key={sport.sportName} className="flex items-center gap-3">
                  <span className={`text-sm w-28 shrink-0 ${theme.text}`}>{sport.sportName}</span>
                  <div className="flex-1 h-2.5 rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,0.08)' }}>
                    <div
                      className="h-full rounded-full bg-blue-500"
                      style={{ width: `${barWidth}%` }}
                    />
                  </div>
                  <span className={`text-sm w-24 text-right shrink-0 ${theme.textMuted}`}>
                    {shown} <span className={`text-xs ${theme.textMuted}`}>({sport.matchesPlayed})</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Individual vs Team Performance */}
      {stats.totalMatches > 0 && (
        <div className={`${theme.card} rounded-lg border ${theme.border} p-6`}>
          <h4 className={`text-md font-semibold ${theme.text} mb-4`}>Performance Breakdown</h4>
          <div className="grid grid-cols-3 gap-6">
            <div>
              <p className={`text-sm ${theme.textMuted} mb-2`}>Individual Play</p>
              <p className={`text-3xl font-bold ${theme.text}`}>
                {formatPercentage(stats.individualWinRate)}
              </p>
              <p className={`text-xs ${theme.textMuted} mt-1`}>
                {stats.individualWins} wins / {stats.individualMatches} matches
              </p>
            </div>
            <div>
              <p className={`text-sm ${theme.textMuted} mb-2`}>Team Play</p>
              <p className={`text-3xl font-bold ${theme.text}`}>
                {formatPercentage(stats.teamWinRate)}
              </p>
              <p className={`text-xs ${theme.textMuted} mt-1`}>
                {stats.teamWins} wins / {stats.teamMatches} matches
              </p>
            </div>
            <div>
              <p className={`text-sm ${theme.textMuted} mb-2 flex items-center gap-1`}>
                <Medal className="w-3.5 h-3.5 text-yellow-500" />
                Top 3 Finishes
              </p>
              <p className={`text-3xl font-bold ${theme.text}`}>
                {stats.topThreeFinishes ?? 0}
              </p>
              <p className={`text-xs ${theme.textMuted} mt-1`}>
                tournaments
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Sports Played */}
      {stats.sportsPlayed && stats.sportsPlayed.length > 0 && (
        <div>
          <h3 className={`text-lg font-semibold ${theme.text} mb-4`}>
            Sports Statistics
          </h3>
          <div className="space-y-3">
            {stats.sportsPlayed.map((sport) => (
              <div
                key={sport.sportName}
                className={`${theme.card} rounded-lg border ${theme.border} ${expandedSport !== sport.sportName ? 'overflow-hidden' : ''}`}
              >
                {/* Sport Header */}
                <button
                  onClick={() => SPORTS_WITH_DETAIL.includes(sport.sportName) ? toggleSport(sport.sportName) : null}
                  className={`w-full px-6 py-4 flex items-center justify-between ${SPORTS_WITH_DETAIL.includes(sport.sportName) ? `${theme.cardHover} cursor-pointer` : 'cursor-default'} transition-colors`}
                >
                  <div className="flex items-center gap-4">
                    <Target className="w-5 h-5 text-blue-500" />
                    <div className="text-left">
                      <h4 className={`font-semibold ${theme.text}`}>{sport.sportName}</h4>
                      <p className={`text-sm ${theme.textMuted}`}>
                        {sport.matchesPlayed} matches • {formatPercentage(sport.winRate)} ({sport.wins}/{sport.matchesPlayed})
                      </p>
                    </div>
                  </div>
                  {SPORTS_WITH_DETAIL.includes(sport.sportName) && (
                    expandedSport === sport.sportName
                      ? <ChevronUp className={`w-5 h-5 ${theme.textMuted}`} />
                      : <ChevronDown className={`w-5 h-5 ${theme.textMuted}`} />
                  )}
                </button>

                {/* Sport Details (Expandable) */}
                {SPORTS_WITH_DETAIL.includes(sport.sportName) && expandedSport === sport.sportName && (
                  <div className={`px-6 py-4 border-t ${theme.border} ${theme.backgroundSecondary}`}>
                    {/* Loading spinner while fetching detail */}
                    {loadingSport === sport.sportName && (
                      <div className="flex items-center justify-center py-6">
                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent" />
                      </div>
                    )}
                    {/* Ping Pong shot type stats */}
                    {!loadingSport && sportDetails[sport.sportName]?.totalRalliesWithShotData !== undefined && (
                      <div className={`mt-4 pt-4 border-t ${theme.border}`}>
                        <p className={`text-xs font-semibold uppercase tracking-wide ${theme.textMuted} mb-3`}>
                          Shot Type Breakdown · {sportDetails[sport.sportName].totalRalliesWithShotData} recorded rallies
                        </p>

                        {/* Winners */}
                        <p className={`text-xs font-medium ${theme.textMuted} mb-2`}>Winners</p>
                        <div className="space-y-1.5 mb-3">
                          {[
                            { label: 'Ace', key: 'ace', color: 'bg-blue-500' },
                            { label: 'Smash', key: 'smash', color: 'bg-purple-500' },
                            { label: 'Spin', key: 'spin', color: 'bg-cyan-500' },
                          ].map(({ label, key, color }) => {
                            const count = sportDetails[sport.sportName][key] || 0;
                            const pct = sportDetails[sport.sportName].totalRalliesWithShotData > 0
                              ? Math.round((count / sportDetails[sport.sportName].totalRalliesWithShotData) * 100)
                              : 0;
                            return (
                              <div key={key} className="flex items-center gap-2">
                                <span className={`text-xs w-16 shrink-0 ${theme.text}`}>{label}</span>
                                <div className="flex-1 h-2 rounded-full overflow-hidden" style={{background: 'rgba(0,0,0,0.1)'}}>
                                  <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
                                </div>
                                <span className={`text-xs w-16 text-right shrink-0 ${theme.text}`}>{count} <span className={theme.textMuted}>({pct}%)</span></span>
                              </div>
                            );
                          })}
                        </div>

                        {/* Errors */}
                        <p className={`text-xs font-medium ${theme.textMuted} mb-2`}>Errors</p>
                        <div className="space-y-1.5">
                          {[
                            { label: 'Fault', key: 'fault', color: 'bg-orange-500' },
                            { label: 'Unforced Error', key: 'unforcedError', color: 'bg-red-500' },
                          ].map(({ label, key, color }) => {
                            const count = sportDetails[sport.sportName][key] || 0;
                            const pct = sportDetails[sport.sportName].totalRalliesWithShotData > 0
                              ? Math.round((count / sportDetails[sport.sportName].totalRalliesWithShotData) * 100)
                              : 0;
                            return (
                              <div key={key} className="flex items-center gap-2">
                                <span className={`text-xs w-16 shrink-0 ${theme.text}`}>{label}</span>
                                <div className="flex-1 h-2 rounded-full overflow-hidden" style={{background: 'rgba(0,0,0,0.1)'}}>
                                  <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
                                </div>
                                <span className={`text-xs w-16 text-right shrink-0 ${theme.text}`}>{count} <span className={theme.textMuted}>({pct}%)</span></span>
                              </div>
                            );
                          })}
                        </div>

                        {/* Summary totals */}
                        <div className="mt-3 flex gap-4">
                          <span className={`text-xs ${theme.textMuted}`}>
                            Winners: <span className="font-semibold text-green-600 dark:text-green-400">{sportDetails[sport.sportName].totalWinners}</span>
                          </span>
                          <span className={`text-xs ${theme.textMuted}`}>
                            Errors: <span className="font-semibold text-red-600 dark:text-red-400">{sportDetails[sport.sportName].totalErrors}</span>
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Cricket detail stats */}
                    {!loadingSport && sport.sportName === 'Cricket' && sportDetails[sport.sportName] && (
                      <div>
                        {/* Batting */}
                        <p className="text-xs font-bold uppercase tracking-widest text-yellow-500 dark:text-yellow-400 mb-2">Batting</p>
                        <div className="overflow-x-auto mb-5 rounded-lg border border-white/10">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="bg-white/10 dark:bg-white/5">
                                {['M','Inn','NO','Runs','HS','Avg','SR','100s','50s','4s','6s'].map(h => (
                                  <th key={h} className={`text-center py-2 px-2 font-semibold tracking-wide whitespace-nowrap ${theme.textMuted}`}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              <tr className={`${theme.text} font-semibold border-t border-white/10`}>
                                <td className="text-center py-2.5 px-2">{sportDetails[sport.sportName].batting?.matches ?? 0}</td>
                                <td className="text-center py-2.5 px-2">{sportDetails[sport.sportName].batting?.innings ?? 0}</td>
                                <td className="text-center py-2.5 px-2">{sportDetails[sport.sportName].batting?.notOuts ?? 0}</td>
                                <td className="text-center py-2.5 px-2 text-green-400 font-bold">{sportDetails[sport.sportName].batting?.totalRuns ?? 0}</td>
                                <td className="text-center py-2.5 px-2 text-blue-400">{sportDetails[sport.sportName].batting?.highestScore ?? '0'}</td>
                                <td className="text-center py-2.5 px-2">{sportDetails[sport.sportName].batting?.battingAverage ?? '—'}</td>
                                <td className="text-center py-2.5 px-2">{sportDetails[sport.sportName].batting?.battingStrikeRate ?? '—'}</td>
                                <td className="text-center py-2.5 px-2 text-yellow-400">{sportDetails[sport.sportName].batting?.hundreds ?? 0}</td>
                                <td className="text-center py-2.5 px-2 text-yellow-400">{sportDetails[sport.sportName].batting?.fifties ?? 0}</td>
                                <td className="text-center py-2.5 px-2">{sportDetails[sport.sportName].batting?.fours ?? 0}</td>
                                <td className="text-center py-2.5 px-2">{sportDetails[sport.sportName].batting?.sixes ?? 0}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {/* Bowling */}
                        <p className="text-xs font-bold uppercase tracking-widest text-blue-400 dark:text-blue-300 mb-2">Bowling</p>
                        <div className="overflow-x-auto rounded-lg border border-white/10">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="bg-white/10 dark:bg-white/5">
                                {['Inn','O','W','R','Econ','Avg','SR','BB','Wd','NB'].map(h => (
                                  <th key={h} className={`text-center py-2 px-2 font-semibold tracking-wide whitespace-nowrap ${theme.textMuted}`}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              <tr className={`${theme.text} font-semibold border-t border-white/10`}>
                                <td className="text-center py-2.5 px-2">{sportDetails[sport.sportName].bowling?.innings ?? 0}</td>
                                <td className="text-center py-2.5 px-2">{sportDetails[sport.sportName].bowling?.overs ?? '0.0'}</td>
                                <td className="text-center py-2.5 px-2 text-green-400 font-bold">{sportDetails[sport.sportName].bowling?.wickets ?? 0}</td>
                                <td className="text-center py-2.5 px-2">{sportDetails[sport.sportName].bowling?.runsConceded ?? 0}</td>
                                <td className="text-center py-2.5 px-2">{sportDetails[sport.sportName].bowling?.economy ?? '—'}</td>
                                <td className="text-center py-2.5 px-2">{sportDetails[sport.sportName].bowling?.bowlingAverage ?? '—'}</td>
                                <td className="text-center py-2.5 px-2">{sportDetails[sport.sportName].bowling?.bowlingSR ?? '—'}</td>
                                <td className="text-center py-2.5 px-2 text-blue-400">{sportDetails[sport.sportName].bowling?.bestBowling ?? '—'}</td>
                                <td className="text-center py-2.5 px-2">{sportDetails[sport.sportName].bowling?.wideBalls ?? 0}</td>
                                <td className="text-center py-2.5 px-2">{sportDetails[sport.sportName].bowling?.noBalls ?? 0}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Basketball detail stats */}
                    {!loadingSport && sport.sportName === 'Basketball' && sportDetails[sport.sportName] && (
                      <div className={`mt-4 pt-4 border-t ${theme.border}`}>
                        <p className={`text-xs font-semibold uppercase tracking-wide ${theme.textMuted} mb-3`}>
                          Career Totals · {sportDetails[sport.sportName].gamesPlayed} games
                        </p>
                        <div className="grid grid-cols-2 gap-x-6">
                          {[
                            { label: 'Points',      value: sportDetails[sport.sportName].totalPoints,         color: 'text-blue-500' },
                            { label: 'Rebounds',    value: sportDetails[sport.sportName].rebounds,             color: 'text-purple-500' },
                            { label: 'Assists',     value: sportDetails[sport.sportName].assists,              color: 'text-green-500' },
                            { label: 'Steals',      value: sportDetails[sport.sportName].steals,               color: 'text-yellow-500' },
                            { label: 'Blocks',      value: sportDetails[sport.sportName].blocks,               color: 'text-red-500' },
                            { label: '3-Pointers',  value: `${sportDetails[sport.sportName].threePointersMade}/${sportDetails[sport.sportName].threePointersMade + sportDetails[sport.sportName].threePointersMissed}`, color: '' },
                            { label: '2-Pointers',  value: `${sportDetails[sport.sportName].twoPointersMade}/${sportDetails[sport.sportName].twoPointersMade + sportDetails[sport.sportName].twoPointersMissed}`,   color: '' },
                            { label: 'Free Throws', value: `${sportDetails[sport.sportName].freeThrowsMade}/${sportDetails[sport.sportName].freeThrowsMade + sportDetails[sport.sportName].freeThrowsMissed}`,      color: '' },
                          ].map(({ label, value, color }) => (
                            <div key={label} className="flex justify-between items-center py-0.5">
                              <span className={`text-xs ${theme.textMuted}`}>{label}</span>
                              <span className={`text-xs font-semibold ${color || theme.text}`}>{value ?? 0}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Pickleball detail stats */}
                    {!loadingSport && sport.sportName === 'Pickleball' && sportDetails[sport.sportName] && (
                      <div>
                        {/* Summary row */}
                        <div className="grid grid-cols-5 gap-2 text-center mb-4">
                          {[
                            { label: 'Matches', value: sportDetails[sport.sportName].matchesPlayed, color: 'text-blue-400' },
                            { label: 'Wins', value: sportDetails[sport.sportName].wins, color: 'text-green-400' },
                            { label: 'Losses', value: sportDetails[sport.sportName].losses, color: 'text-red-400' },
                            { label: 'Games Won', value: sportDetails[sport.sportName].gamesWon ?? 0, color: 'text-green-400' },
                            { label: 'Games Lost', value: sportDetails[sport.sportName].gamesLost ?? 0, color: 'text-red-400' },
                          ].map(({ label, value, color }) => (
                            <div key={label} className={`${theme.card} rounded-lg p-2 border ${theme.border}`}>
                              <p className={`text-lg font-bold ${color}`}>{value ?? 0}</p>
                              <p className={`text-xs ${theme.textMuted} mt-0.5`}>{label}</p>
                            </div>
                          ))}
                        </div>
                        {/* Match history */}
                        {sportDetails[sport.sportName].matchHistory?.length > 0 && (
                          <div className={`rounded-lg border ${theme.border} overflow-hidden`}>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="bg-white/10 dark:bg-white/5">
                                  {['Date', 'Opponent', 'Score', 'Result'].map(h => (
                                    <th key={h} className={`text-left py-2 px-3 font-semibold ${theme.textMuted}`}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {sportDetails[sport.sportName].matchHistory.map((m, i) => (
                                  <tr key={m.gameId} className={`border-t border-white/10 ${i % 2 === 0 ? '' : 'bg-white/5'}`}>
                                    <td className={`py-2 px-3 ${theme.textMuted}`}>{new Date(m.date).toLocaleDateString()}</td>
                                    <td className={`py-2 px-3 ${theme.text}`}>{m.opponent}</td>
                                    <td className={`py-2 px-3 font-mono ${theme.text}`}>{m.scores.join(', ') || '—'}</td>
                                    <td className="py-2 px-3">
                                      <span className={`px-2 py-0.5 rounded-full font-bold text-xs ${
                                        m.result === 'W'
                                          ? 'bg-green-900/50 text-green-400'
                                          : 'bg-red-900/50 text-red-400'
                                      }`}>{m.result}</span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Volleyball detail stats */}
                    {!loadingSport && sport.sportName === 'Volleyball' && sportDetails[sport.sportName] && (
                      <div>
                        <div className="grid grid-cols-5 gap-2 text-center mb-4">
                          {[
                            { label: 'Matches', value: sportDetails[sport.sportName].matchesPlayed, color: 'text-blue-400' },
                            { label: 'Wins', value: sportDetails[sport.sportName].wins, color: 'text-green-400' },
                            { label: 'Losses', value: sportDetails[sport.sportName].losses, color: 'text-red-400' },
                            { label: 'Sets Won', value: sportDetails[sport.sportName].setsWon ?? 0, color: 'text-green-400' },
                            { label: 'Sets Lost', value: sportDetails[sport.sportName].setsLost ?? 0, color: 'text-red-400' },
                          ].map(({ label, value, color }) => (
                            <div key={label} className={`${theme.card} rounded-lg p-2 border ${theme.border}`}>
                              <p className={`text-lg font-bold ${color}`}>{value ?? 0}</p>
                              <p className={`text-xs ${theme.textMuted} mt-0.5`}>{label}</p>
                            </div>
                          ))}
                        </div>
                        {sportDetails[sport.sportName].matchHistory?.length > 0 && (
                          <div className={`rounded-lg border ${theme.border} overflow-hidden`}>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="bg-white/10 dark:bg-white/5">
                                  {['Date', 'Opponent', 'Sets', 'Result'].map(h => (
                                    <th key={h} className={`text-left py-2 px-3 font-semibold ${theme.textMuted}`}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {sportDetails[sport.sportName].matchHistory.map((m, i) => (
                                  <tr key={m.gameId} className={`border-t border-white/10 ${i % 2 === 0 ? '' : 'bg-white/5'}`}>
                                    <td className={`py-2 px-3 ${theme.textMuted}`}>{new Date(m.date).toLocaleDateString()}</td>
                                    <td className={`py-2 px-3 ${theme.text}`}>{m.opponent}</td>
                                    <td className={`py-2 px-3 font-mono ${theme.text}`}>{m.scores.join(', ') || '—'}</td>
                                    <td className="py-2 px-3">
                                      <span className={`px-2 py-0.5 rounded-full font-bold text-xs ${
                                        m.result === 'W' ? 'bg-green-900/50 text-green-400' : 'bg-red-900/50 text-red-400'
                                      }`}>{m.result}</span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Tennis detail stats */}
                    {!loadingSport && sport.sportName === 'Tennis' && sportDetails[sport.sportName] && (
                      <div className={`mt-4 pt-4 border-t ${theme.border}`}>
                        <div className="grid grid-cols-2 gap-6">
                          <div>
                            <p className={`text-xs font-semibold uppercase tracking-wide ${theme.textMuted} mb-3`}>Serve</p>
                            {[
                              { label: 'Aces',            value: sportDetails[sport.sportName].aces },
                              { label: 'Double Faults',   value: sportDetails[sport.sportName].doubleFaults },
                              { label: '1st Srv Faults',  value: sportDetails[sport.sportName].firstServeFaults },
                              { label: 'Service Hold',    value: sportDetails[sport.sportName].serviceGamesPlayed > 0
                                ? `${Math.round((sportDetails[sport.sportName].serviceGamesHeld / sportDetails[sport.sportName].serviceGamesPlayed) * 100)}%`
                                : '—' },
                              { label: 'Pts Won on Srv',  value: sportDetails[sport.sportName].pointsWonOnServe },
                            ].map(({ label, value }) => (
                              <div key={label} className="flex justify-between items-center py-0.5">
                                <span className={`text-xs ${theme.textMuted}`}>{label}</span>
                                <span className={`text-xs font-semibold ${theme.text}`}>{value ?? 0}</span>
                              </div>
                            ))}
                          </div>
                          <div>
                            <p className={`text-xs font-semibold uppercase tracking-wide ${theme.textMuted} mb-3`}>Rally</p>
                            {[
                              { label: 'Winners',         value: sportDetails[sport.sportName].winners },
                              { label: 'Unforced Errors', value: sportDetails[sport.sportName].unforcedErrors },
                              { label: 'Forced Errors',   value: sportDetails[sport.sportName].forcedErrors },
                              { label: 'Points Won',      value: sportDetails[sport.sportName].pointsWon },
                              { label: 'Pts on Return',   value: sportDetails[sport.sportName].pointsWonOnReturn },
                              { label: 'Breaks of Serve', value: sportDetails[sport.sportName].breaksOfServe },
                              { label: 'Tiebreak Pts',    value: sportDetails[sport.sportName].tiebreakPointsWon },
                            ].map(({ label, value }) => (
                              <div key={label} className="flex justify-between items-center py-0.5">
                                <span className={`text-xs ${theme.textMuted}`}>{label}</span>
                                <span className={`text-xs font-semibold ${theme.text}`}>{value ?? 0}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Matches */}
      {stats.recentMatches && stats.recentMatches.length > 0 && (
        <div>
          <h3 className={`text-lg font-semibold ${theme.text} mb-4`}>
            Recent Activity
          </h3>
          <div className={`${theme.card} rounded-lg border ${theme.border} overflow-hidden`}>
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {stats.recentMatches.slice(0, 5).map((match) => (
                <div
                  key={match.matchId}
                  onClick={() => match.tournamentId && navigate(`/tournament/${match.tournamentId}`)}
                  className={`px-6 py-4 flex items-center justify-between ${match.tournamentId ? `cursor-pointer hover:${theme.cardHover} transition-colors` : ''}`}
                >
                  <div className="flex-1">
                    <p className={`font-medium ${theme.text} flex items-center gap-1.5`}>
                      {match.tournamentName}
                      {match.tournamentId && <ExternalLink className={`w-3.5 h-3.5 ${theme.textMuted}`} />}
                    </p>
                    <p className={`text-sm ${theme.textMuted}`}>
                      {new Date(match.date).toLocaleDateString()} • {match.type}
                    </p>
                  </div>
                  {/* The sport, second column. Every row said "Casual Game" and
                      a score, which is the one thing that does not identify a
                      match — a player who plays five sports could not tell
                      which of them a row belonged to. */}
                  <div className="w-28 shrink-0 px-2">
                    {match.sport && (
                      <span className={`text-sm ${theme.textSecondary ?? theme.textMuted}`}>{match.sport}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-lg font-semibold ${theme.textMuted}`}>
                      {match.score}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                      match.result === 'W'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}>
                      {match.result}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
